import {
  LaunchBrowserInputSchema,
  LAUNCH_BROWSER_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import { launchProxiedBrowser } from '../../../../utils/launchProxiedBrowser';
import {
  filterApplicationCredentials,
  listApplicationCredentials,
} from '../../../../utils/listApplicationCredentials';
import {
  getElectronApiUnavailableResult,
  isElectronApiAvailable,
  parsePortalToolHandlerInput,
  resolvePortalApplicationId,
} from '../portal-tool-handler.utils';
import type { PortalToolHandler } from '../types';

function summarizeBrowserInstances({
  instances,
}: {
  instances: Record<string, unknown>;
}) {
  return Object.entries(instances).map(([instanceId, value]) => {
    const isRunning =
      typeof value === 'object' &&
      value !== null &&
      typeof (value as { isRunning?: unknown }).isRunning === 'boolean' &&
      (value as { isRunning: boolean }).isRunning === true;

    return {
      instanceId,
      isRunning,
    };
  });
}

export const launchBrowserHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
  applicationId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: LaunchBrowserInputSchema,
    input,
    toolName: LAUNCH_BROWSER_TOOL_NAME,
    toolCallId,
    batchId,
  });
  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  const action = parsedInput.data.action ?? 'launch';

  if (action === 'list-credentials') {
    const resolvedApplicationId = resolvePortalApplicationId({ applicationId });
    const credentialsResult = await listApplicationCredentials({
      applicationId: resolvedApplicationId,
    });

    if (!credentialsResult.success) {
      return {
        status: 'error',
        result: { message: credentialsResult.error },
      };
    }

    const credentials = filterApplicationCredentials({
      credentials: credentialsResult.credentials,
      nameFilter: parsedInput.data.nameFilter,
    });
    const filterSuffix = parsedInput.data.nameFilter?.trim()
      ? ` matching "${parsedInput.data.nameFilter.trim()}"`
      : '';

    return {
      status: 'success',
      result: {
        message:
          credentials.length === 0
            ? `No target credentials found${filterSuffix}`
            : `Listed ${credentials.length} application credential${credentials.length === 1 ? '' : 's'}${filterSuffix}`,
        data: { credentials },
      },
    };
  }

  if (!isElectronApiAvailable()) {
    return getElectronApiUnavailableResult({
      toolName: LAUNCH_BROWSER_TOOL_NAME,
      toolCallId,
      batchId,
    });
  }

  if (action === 'list-instances') {
    const instancesResult = await window.electronAPI.browser.getInstances();
    if (!instancesResult.success || !instancesResult.data) {
      return {
        status: 'error',
        result: {
          message: instancesResult.error || 'Failed to list browser instances',
        },
      };
    }

    const instances = summarizeBrowserInstances({
      instances: instancesResult.data.instances,
    });

    return {
      status: 'success',
      result: {
        message:
          instances.length === 0
            ? 'No active browser instances'
            : `Listed ${instances.length} browser instance${instances.length === 1 ? '' : 's'}`,
        data: { instances },
      },
    };
  }

  if (action === 'list-pages') {
    const instanceId = parsedInput.data.instanceId;
    if (!instanceId) {
      return {
        status: 'error',
        result: { message: 'instanceId is required for list-pages action' },
      };
    }

    const pagesResult = await window.electronAPI.browser.getPages({
      instanceId,
    });

    if (!pagesResult.success || !pagesResult.data) {
      return {
        status: 'error',
        result: {
          message: pagesResult.error || 'Failed to list browser pages',
        },
      };
    }

    const pages = pagesResult.data.pages;

    return {
      status: 'success',
      result: {
        message:
          pages.length === 0
            ? `No pages found for instance ${parsedInput.data.instanceId}`
            : `Listed ${pages.length} page${pages.length === 1 ? '' : 's'} for instance ${parsedInput.data.instanceId}`,
        data: {
          instanceId: parsedInput.data.instanceId,
          pages,
        },
      },
    };
  }

  const launchResult = await launchProxiedBrowser({
    startUrl: parsedInput.data.startUrl,
    instanceId: parsedInput.data.instanceId,
    credentialId: parsedInput.data.credentialId,
    freshSession: parsedInput.data.freshSession,
    applicationId: resolvePortalApplicationId({ applicationId }),
  });

  if (!launchResult.success) {
    return {
      status: 'error',
      result: {
        message: launchResult.error,
      },
    };
  }

  const message = parsedInput.data.instanceId
    ? launchResult.navigatedUrl
      ? `Opened new tab at ${launchResult.navigatedUrl}`
      : 'Opened new tab in existing browser instance'
    : launchResult.focusedExisting
      ? 'Focused existing browser instance'
      : 'Launched proxied browser';

  return {
    status: 'success',
    result: {
      message,
      data: {
        instanceId: launchResult.instanceId,
        pageId: launchResult.pageId,
        startUrl: launchResult.startUrl,
        navigatedUrl: launchResult.navigatedUrl,
        focusedExisting: launchResult.focusedExisting,
        credentialInjected: launchResult.credentialInjected,
      },
    },
  };
};
